import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  DateInput,
  EditButton,
  Show,
  SimpleShowLayout,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  Filter,
  TopToolbar,
  ExportButton,
  BulkUpdateButton,
  BulkExportButton,
  FunctionField,
  ArrayField,
  SingleFieldList,
  ChipField,
  useRecordContext,
  BooleanField,
  SaveButton,
  Toolbar,
  useNotify,
  useRefresh,
  useListContext,
  ShowButton,
  required,
} from 'react-admin';
import {
  Box,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  Button as MuiButton,
  useMediaQuery,
  Theme,
  Collapse,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  TextField as MuiTextField,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// 모바일용 필터 컴포넌트
const MobileOrderFilter = (props: any) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {/* 기본 필터 (항상 표시) */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Filter {...props}>
          <TextInput
            label="주문번호"
            source="order_number"
            alwaysOn
            size="small"
            sx={{
              '& .MuiInputBase-root': { height: 40 },
              minWidth: 120,
              flex: 1,
            }}
          />
          <SelectInput
            source="status"
            label="상태"
            alwaysOn
            size="small"
            choices={[
              { id: 'PAID', name: '결제완료' },
              { id: 'PROCESSING', name: '준비중' },
              { id: 'SHIPPED', name: '배송중' },
              { id: 'DELIVERED', name: '배송완료' },
              { id: 'CANCELLED', name: '취소' },
              { id: 'REFUNDED', name: '환불' },
            ]}
            sx={{
              '& .MuiInputBase-root': { height: 40 },
              minWidth: 100,
            }}
          />
        </Filter>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{
            border: '1px solid #ddd',
            borderRadius: 1,
            p: 1,
          }}
        >
          <FilterListIcon fontSize="small" />
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* 추가 필터 (접기/펼치기) */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Filter {...props}>
            <TextInput
              label="수취인"
              source="orderer_name"
              alwaysOn
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
            <TextInput
              label="핸드폰번호"
              source="orderer_phone"
              alwaysOn
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
            <SelectInput
              source="payment_method"
              label="결제수단"
              alwaysOn
              size="small"
              fullWidth
              choices={[
                { id: 'CARD', name: '신용카드' },
                { id: 'BANK_TRANSFER', name: '무통장입금' },
                { id: 'VIRTUAL_ACCOUNT', name: '가상계좌' },
                { id: 'PHONE', name: '휴대폰결제' },
                { id: 'KAKAO_PAY', name: '카카오페이' },
                { id: 'NAVER_PAY', name: '네이버페이' },
                { id: 'TOSS', name: '토스' },
              ]}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <DateInput
                source="created_at_gte"
                label="시작일"
                alwaysOn
                size="small"
                sx={{ flex: 1 }}
              />
              <DateInput
                source="created_at_lte"
                label="종료일"
                alwaysOn
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
          </Filter>
        </Box>
      </Collapse>
    </Box>
  );
};

// 데스크톱용 주문 필터
const OrderFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="주문번호" source="order_number" alwaysOn />
    <TextInput label="수취인" source="orderer_name" />
    <TextInput label="핸드폰번호" source="orderer_phone" />
    <SelectInput
      source="status"
      label="주문상태"
      choices={[
        { id: 'PAID', name: '결제완료' },
        { id: 'PROCESSING', name: '상품준비중' },
        { id: 'SHIPPED', name: '배송중' },
        { id: 'DELIVERED', name: '배송완료' },
        { id: 'CANCELLED', name: '취소' },
        { id: 'REFUNDED', name: '환불' },
      ]}
    />
    <SelectInput
      source="payment_method"
      label="결제수단"
      choices={[
        { id: 'CARD', name: '신용카드' },
        { id: 'BANK_TRANSFER', name: '무통장입금' },
        { id: 'VIRTUAL_ACCOUNT', name: '가상계좌' },
        { id: 'PHONE', name: '휴대폰결제' },
        { id: 'KAKAO_PAY', name: '카카오페이' },
        { id: 'NAVER_PAY', name: '네이버페이' },
        { id: 'TOSS', name: '토스' },
      ]}
    />
    <DateInput source="created_at_gte" label="주문일시 시작" />
    <DateInput source="created_at_lte" label="주문일시 종료" />
  </Filter>
);

// 주문 내보내기 (한국어 컬럼명)
const orderExporter = (orders: any[]) => {
  const ordersForExport = orders.map(order => ({
    '주문번호': order.orderNumber,
    '주문일시': order.created_at,
    '회원구분': order.userId ? '회원' : '비회원',
    '주문자명': order.ordererName,
    '주문자이메일': order.ordererEmail || order.userEmail,
    '주문자연락처': order.ordererPhone,
    '수취인': order.recipientName,
    '수취인연락처': order.recipientPhone,
    '우편번호': order.postcode,
    '배송주소': order.address,
    '상세주소': order.addressDetail,
    '배송메모': order.shippingMemo || order.deliveryRequest,
    '상품명': order.items?.map((item: any) => item.product_name).join(', '),
    '상품금액': order.subtotal || order.productAmount,
    '할인금액': order.discountAmount,
    '배송비': order.shippingFee,
    '쿠폰할인': order.couponDiscount,
    '포인트사용': order.pointUsed || order.pointsUsed,
    '총결제금액': order.finalAmount,
    '결제수단': (() => {
      const methods: any = {
        CARD: '신용카드',
        BANK_TRANSFER: '무통장입금',
        VIRTUAL_ACCOUNT: '가상계좌',
        PHONE: '휴대폰결제',
        KAKAO_PAY: '카카오페이',
        NAVER_PAY: '네이버페이',
        TOSS: '토스',
        TOSS_PAY: '토스페이',
        TRANSFER: '계좌이체',
      };
      return methods[order.paymentMethod] || order.paymentMethod;
    })(),
    '결제상태': order.paymentStatus === 'PAID' ? '결제완료' : order.paymentStatus,
    '주문상태': (() => {
      const statuses: any = {
        PAID: '결제완료',
        PROCESSING: '상품준비중',
        SHIPPED: '배송중',
        DELIVERED: '배송완료',
        CANCELLED: '취소',
        REFUNDED: '환불',
      };
      return statuses[order.status] || order.status;
    })(),
    '택배사': order.shippingCompany,
    '송장번호': order.trackingNumber,
    '결제키': order.paymentKey,
  }));

  // CSV 생성 및 다운로드
  const headers = Object.keys(ordersForExport[0] || {});
  const csvContent = [
    headers.join(','),
    ...ordersForExport.map(row =>
      headers.map(header => {
        const value = row[header];
        // 쉼표나 줄바꿈이 있으면 따옴표로 감싸기
        if (value && (String(value).includes(',') || String(value).includes('\n'))) {
          return `"${String(value).replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // BOM 추가 (Excel에서 한글 깨짐 방지)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `주문목록_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// 리스트 액션 툴바
const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

// 벌크 액션 버튼
const BulkActionButtons = () => (
  <>
    <BulkUpdateButton
      label="상태 변경"
      data={{ status: 'PROCESSING' }}
      mutationMode="pessimistic"
    />
    <BulkExportButton />
  </>
);

// 주문 상태 표시
const OrderStatusChip = ({ status, compact = false }: { status: string; compact?: boolean }) => {
  const statusConfig: Record<string, { label: string; shortLabel: string; bgColor: string; textColor: string }> = {
    PENDING: { label: '결제대기', shortLabel: '대기', bgColor: '#FFF3E0', textColor: '#E65100' },
    PAID: { label: '결제완료', shortLabel: '결제', bgColor: '#E8F5E9', textColor: '#2E7D32' },
    PROCESSING: { label: '상품준비중', shortLabel: '준비', bgColor: '#E3F2FD', textColor: '#1565C0' },
    SHIPPED: { label: '배송중', shortLabel: '배송', bgColor: '#F3E5F5', textColor: '#7B1FA2' },
    DELIVERED: { label: '배송완료', shortLabel: '완료', bgColor: '#E0F2F1', textColor: '#00695C' },
    COMPLETED: { label: '구매확정', shortLabel: '확정', bgColor: '#ECEFF1', textColor: '#37474F' },
    CANCELLED: { label: '취소', shortLabel: '취소', bgColor: '#FFEBEE', textColor: '#C62828' },
    REFUNDED: { label: '환불', shortLabel: '환불', bgColor: '#FAFAFA', textColor: '#616161' },
  };

  const config = statusConfig[status] || { label: status, shortLabel: status, bgColor: '#F5F5F5', textColor: '#757575' };

  return (
    <Chip
      label={compact ? config.shortLabel : config.label}
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 600,
        ...(compact ? {
          height: 20,
          fontSize: '0.65rem',
          '& .MuiChip-label': { px: 0.75 },
        } : {}),
      }}
    />
  );
};

// 모바일용 주문 목록 컴포넌트
const MobileOrderList = () => {
  const { data, isLoading } = useListContext();

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">주문 내역이 없습니다.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden', px: 0.5 }}>
      {data.map((record: any) => (
        <Card
          key={record.id}
          sx={{
            mb: 1,
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: '1px solid #eee',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: '8px !important' }}>
            {/* 헤더: 주문번호 + 상태 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  letterSpacing: '-0.3px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 'calc(100% - 40px)',
                }}
              >
                {record.orderNumber}
              </Typography>
              <OrderStatusChip status={record.status} compact />
            </Box>

            {/* 금액 + 날짜 */}
            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.95rem', color: '#333' }}>
                {Number(record.finalAmount || 0).toLocaleString()}원
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem', flexShrink: 0 }}>
                {new Date(record.created_at).getMonth() + 1}/{new Date(record.created_at).getDate()} {String(new Date(record.created_at).getHours()).padStart(2, '0')}:{String(new Date(record.created_at).getMinutes()).padStart(2, '0')}
              </Typography>
            </Box>

            {/* 주문자 + 결제수단 (한 줄로) */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                bgcolor: '#f8f8f8',
                borderRadius: 1,
                px: 0.5,
                py: 0.4,
                mb: 0.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.25} sx={{ overflow: 'hidden', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.ordererName || record.userName || '비회원'}
                </Typography>
                {record.userId && (
                  <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#7c3aed', flexShrink: 0 }}>
                    회원
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', flexShrink: 0 }}>
                {(() => {
                  const methods: any = {
                    CARD: '카드',
                    BANK_TRANSFER: '무통장',
                    VIRTUAL_ACCOUNT: '가상',
                    PHONE: '휴대폰',
                    KAKAO_PAY: '카카오',
                    NAVER_PAY: '네이버',
                    TOSS: '토스',
                    TOSS_PAY: '토스',
                    TRANSFER: '이체',
                  };
                  return methods[record.paymentMethod] || record.paymentMethod;
                })()}
              </Typography>
            </Box>

            {/* 주문상품 (간소화) */}
            {record.items && record.items.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.65rem',
                  color: '#555',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.items[0]?.product_name}
                {record.items.length > 1 && (
                  <Typography component="span" sx={{ fontSize: '0.6rem', color: '#888', ml: 0.5 }}>
                    외 {record.items.length - 1}건
                  </Typography>
                )}
              </Typography>
            )}
          </CardContent>

          {/* 액션 버튼 */}
          <Box
            sx={{
              display: 'flex',
              borderTop: '1px solid #eee',
            }}
          >
            <MuiButton
              component="a"
              href={`#/orders/${record.id}/show`}
              size="small"
              sx={{
                flex: 1,
                py: 0.75,
                fontSize: '0.7rem',
                color: '#666',
                borderRadius: 0,
                minHeight: 32,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              상세
            </MuiButton>
            <Divider orientation="vertical" flexItem />
            <MuiButton
              component="a"
              href={`#/orders/${record.id}`}
              size="small"
              sx={{
                flex: 1,
                py: 0.75,
                fontSize: '0.7rem',
                color: '#7c3aed',
                fontWeight: 600,
                borderRadius: 0,
                minHeight: 32,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              관리
            </MuiButton>
          </Box>
        </Card>
      ))}
    </Box>
  );
};

// 주문 목록
export const OrderList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <List
      title="주문 목록"
      filters={<OrderFilter />}
      actions={<ListActions />}
      bulkActionButtons={isSmall ? false : <BulkActionButtons />}
      perPage={isSmall ? 10 : 25}
      sort={{ field: 'created_at', order: 'DESC' }}
      exporter={orderExporter}
      component={isSmall ? "div" : undefined}
      queryOptions={{ refetchOnMount: true }}
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        '& .RaList-content': {
          boxShadow: { xs: 'none', md: 1 },
          borderRadius: { xs: 0, md: 1 },
          overflow: 'hidden',
          maxWidth: '100%',
        },
        '& .RaList-main': {
          px: { xs: 0, md: 2 },
          maxWidth: '100%',
          overflow: 'hidden',
        },
        // 모바일 필터 스타일
        '& .RaFilter-form': {
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: { xs: 0.5, md: 2 },
          px: { xs: 0.5, md: 0 },
          '& .MuiFormControl-root': {
            minWidth: { xs: 'calc(50% - 4px)', md: 'auto' },
          },
        },
      }}
    >
        {isSmall ? (
          <MobileOrderList />
        ) : (
          <Datagrid
            rowClick="show"
            sx={{
              '& .RaDatagrid-headerCell': {
                whiteSpace: 'nowrap',
                textAlign: 'center',
              },
              '& .RaDatagrid-rowCell': {
                textAlign: 'center',
              },
            }}
          >
            <TextField source="orderNumber" label="주문번호" />
            <DateField source="created_at" label="주문일시" showTime />
            <FunctionField
              label="회원"
              render={(record: any) => record?.userId ? '회원' : '비회원'}
            />
            <TextField source="ordererName" label="주문자" />
            <TextField source="recipientName" label="수취인" />
            <TextField source="recipientPhone" label="연락처" />
            <ArrayField source="items" label="상품">
              <SingleFieldList linkType={false} sx={{ pointerEvents: 'none' }}>
                <ChipField source="product_name" />
              </SingleFieldList>
            </ArrayField>
            <FunctionField
              label="총 금액"
              render={(record: any) => `${Number(record?.finalAmount || 0).toLocaleString()}원`}
            />
            <FunctionField
              label="결제수단"
              render={(record: any) => {
                const methods: any = {
                  CARD: '신용카드',
                  BANK_TRANSFER: '무통장입금',
                  VIRTUAL_ACCOUNT: '가상계좌',
                  PHONE: '휴대폰결제',
                  KAKAO_PAY: '카카오페이',
                  NAVER_PAY: '네이버페이',
                  TOSS: '토스',
                  TOSS_PAY: '토스페이',
                  TRANSFER: '계좌이체',
                };
                return methods[record.paymentMethod] || record.paymentMethod;
              }}
            />
            <FunctionField
              label="상태"
              render={(record: any) => <OrderStatusChip status={record.status} />}
            />
            <EditButton label="관리" />
          </Datagrid>
        )}
    </List>
  );
};

// 주문 액션 버튼 컴포넌트
const OrderActionButtons = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  // 주문서 출력
  const handlePrintOrder = () => {
    const printContent = `
      <html>
        <head>
          <title>주문서 - ${record.orderNumber}</title>
          <style>
            body { font-family: 'Malgun Gothic', sans-serif; padding: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .total { font-weight: bold; font-size: 1.2em; }
          </style>
        </head>
        <body>
          <h1>주문서</h1>
          <table>
            <tr><th>주문번호</th><td>${record.orderNumber}</td></tr>
            <tr><th>주문일시</th><td>${new Date(record.created_at).toLocaleString('ko-KR')}</td></tr>
            <tr><th>주문자</th><td>${record.ordererName || record.userName}</td></tr>
            <tr><th>연락처</th><td>${record.ordererPhone || record.userPhone}</td></tr>
            <tr><th>이메일</th><td>${record.ordererEmail || record.userEmail}</td></tr>
          </table>
          <h2>배송 정보</h2>
          <table>
            <tr><th>수취인</th><td>${record.recipientName}</td></tr>
            <tr><th>연락처</th><td>${record.recipientPhone}</td></tr>
            <tr><th>주소</th><td>[${record.postcode}] ${record.address} ${record.addressDetail || ''}</td></tr>
            <tr><th>배송메모</th><td>${record.deliveryRequest || '-'}</td></tr>
          </table>
          <h2>주문 상품</h2>
          <table>
            <tr><th>상품명</th><th>수량</th><th>금액</th></tr>
            ${(record.items || []).map((item: any) => `
              <tr>
                <td>${item.product_name}</td>
                <td>${item.quantity}</td>
                <td>${Number(item.subtotal).toLocaleString()}원</td>
              </tr>
            `).join('')}
          </table>
          <h2>결제 정보</h2>
          <table>
            <tr><th>상품금액</th><td>${Number(record.subtotal || record.productAmount).toLocaleString()}원</td></tr>
            <tr><th>할인금액</th><td>-${Number(record.discountAmount || 0).toLocaleString()}원</td></tr>
            <tr><th>배송비</th><td>+${Number(record.shippingFee || 0).toLocaleString()}원</td></tr>
            <tr class="total"><th>총 결제금액</th><td>${Number(record.finalAmount).toLocaleString()}원</td></tr>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // 거래명세서 출력
  const handlePrintReceipt = () => {
    const printContent = `
      <html>
        <head>
          <title>거래명세서 - ${record.orderNumber}</title>
          <style>
            body { font-family: 'Malgun Gothic', sans-serif; padding: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .total { font-weight: bold; font-size: 1.2em; }
            .header { display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>
          <h1>거래명세서</h1>
          <p>거래일자: ${new Date(record.created_at).toLocaleDateString('ko-KR')}</p>
          <p>주문번호: ${record.orderNumber}</p>
          <hr/>
          <h3>공급받는자</h3>
          <p>상호: ${record.ordererName || record.userName}</p>
          <p>연락처: ${record.ordererPhone || record.userPhone}</p>
          <hr/>
          <h3>품목</h3>
          <table>
            <tr><th>품명</th><th>수량</th><th>단가</th><th>공급가액</th></tr>
            ${(record.items || []).map((item: any) => `
              <tr>
                <td>${item.product_name}</td>
                <td>${item.quantity}</td>
                <td>${Number(item.discount_price || item.product_price).toLocaleString()}원</td>
                <td>${Number(item.subtotal).toLocaleString()}원</td>
              </tr>
            `).join('')}
          </table>
          <table>
            <tr><th>공급가액</th><td>${Number(record.subtotal || record.productAmount).toLocaleString()}원</td></tr>
            <tr><th>할인</th><td>-${Number(record.discountAmount || 0).toLocaleString()}원</td></tr>
            <tr><th>배송비</th><td>+${Number(record.shippingFee || 0).toLocaleString()}원</td></tr>
            <tr class="total"><th>합계금액</th><td>${Number(record.finalAmount).toLocaleString()}원</td></tr>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // 이메일 발송
  const handleSendEmail = () => {
    const email = record.ordererEmail || record.userEmail;
    if (!email) {
      notify('이메일 주소가 없습니다.', { type: 'error' });
      return;
    }
    const subject = encodeURIComponent(`[캣츄] 주문 관련 안내 - ${record.orderNumber}`);
    const body = encodeURIComponent(`
안녕하세요, ${record.ordererName || record.userName}님.

주문번호: ${record.orderNumber}
주문일시: ${new Date(record.created_at).toLocaleString('ko-KR')}
결제금액: ${Number(record.finalAmount).toLocaleString()}원

감사합니다.
캣츄 드림
    `);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  // 주문 취소
  const handleCancelOrder = async () => {
    if (!window.confirm('정말로 이 주문을 취소하시겠습니까?')) return;

    try {
      // 1. 결제된 주문인 경우 결제 취소 먼저 수행
      if (record.paymentKey && record.paymentStatus === 'PAID') {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const cancelResponse = await fetch(`${API_URL}/api/v1/payments/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentKey: record.paymentKey,
            cancelReason: '관리자 주문 취소'
          })
        });

        const cancelResult = await cancelResponse.json();

        if (!cancelResponse.ok || !cancelResult.success) {
          throw new Error(cancelResult.error || '결제 취소에 실패했습니다.');
        }
      }

      // 2. 주문 상태 변경
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/orders?id=eq.${record.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          status: 'CANCELLED',
          cancelled_at: new Date().toISOString()
        })
      });

      if (!response.ok) throw new Error('주문 상태 변경 실패');

      const hasPayment = record.paymentKey && record.paymentStatus === 'PAID';
      notify(hasPayment ? '주문이 취소되었습니다. (결제 취소 완료)' : '주문이 취소되었습니다.', { type: 'success' });
      refresh();
    } catch (error: any) {
      console.error('Cancel order error:', error);
      notify(`주문 취소 실패: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
          관리 기능
        </Typography>
        <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
          <MuiButton
            variant="contained"
            startIcon={<PrintIcon />}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            onClick={handlePrintOrder}
          >
            주문서 출력
          </MuiButton>
          <MuiButton
            variant="contained"
            startIcon={<ReceiptIcon />}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            onClick={handlePrintReceipt}
          >
            거래명세서 출력
          </MuiButton>
          <MuiButton
            variant="contained"
            startIcon={<EmailIcon />}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            onClick={handleSendEmail}
          >
            이메일 발송
          </MuiButton>
          <MuiButton
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            onClick={handleCancelOrder}
            disabled={record.status === 'CANCELLED' || record.status === 'REFUNDED'}
          >
            주문 취소
          </MuiButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// 주문 상태에 따른 Stepper activeStep 계산
const getActiveStep = (status: string): number => {
  switch (status) {
    case 'PAID':
      return 0; // 결제완료
    case 'PROCESSING':
      return 1; // 상품준비중
    case 'SHIPPED':
      return 2; // 배송중
    case 'DELIVERED':
      return 3; // 배송완료
    case 'CANCELLED':
    case 'REFUNDED':
      return -1; // 취소/환불
    default:
      return 0;
  }
};

// 주문 상세보기
export const OrderShow = () => {
  const OrderShowTitle = () => {
    const record = useRecordContext();
    return <span>주문 상세: {record?.orderNumber}</span>;
  };

  // 주문 상태 Stepper 컴포넌트
  const OrderStatusStepper = () => {
    const record = useRecordContext();
    if (!record) return null;

    const activeStep = getActiveStep(record.status);

    return (
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
          },
        }}
      >
        <Step>
          <StepLabel>결제완료</StepLabel>
        </Step>
        <Step>
          <StepLabel>상품준비중</StepLabel>
        </Step>
        <Step>
          <StepLabel>배송중</StepLabel>
        </Step>
        <Step>
          <StepLabel>배송완료</StepLabel>
        </Step>
      </Stepper>
    );
  };

  // 배송 조회 컴포넌트
  const DeliveryTrackingSection = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const [trackingInfo, setTrackingInfo] = useState<{
      lastStatus?: string;
      lastTime?: string;
      events?: Array<{
        time: string;
        status: { name: string };
        location: { name: string };
        description: string;
      }>;
    } | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [inputTrackingNumber, setInputTrackingNumber] = useState<string>('');

    const SHIPPING_COMPANIES = [
      { value: 'CJ대한통운', label: 'CJ대한통운' },
      { value: '한진택배', label: '한진택배' },
      { value: '롯데택배', label: '롯데택배' },
      { value: '우체국택배', label: '우체국택배' },
      { value: '로젠택배', label: '로젠택배' },
      { value: '경동택배', label: '경동택배' },
      { value: '대신택배', label: '대신택배' },
      { value: '일양로지스', label: '일양로지스' },
      { value: '합동택배', label: '합동택배' },
      { value: 'GS포스트박스', label: 'GS포스트박스' },
    ];

    // 영문 코드 -> 한글 택배사명 변환 (기존 데이터 호환성)
    const CARRIER_CODE_TO_NAME: { [key: string]: string } = {
      'CJ': 'CJ대한통운',
      'HANJIN': '한진택배',
      'LOTTE': '롯데택배',
      'POST': '우체국택배',
      'LOGEN': '로젠택배',
      'CU': 'CU편의점택배',
      'GS': 'GS편의점택배',
    };

    // 택배사 코드를 정규화 (영문 코드면 한글로 변환)
    const normalizeCarrierName = (carrier: string): string => {
      if (!carrier) return '';
      return CARRIER_CODE_TO_NAME[carrier] || carrier;
    };

    // 레코드에서 초기값 설정
    React.useEffect(() => {
      if (record) {
        const rawCompany = record.shippingCompany || record.shipping_company || '';
        setSelectedCompany(normalizeCarrierName(rawCompany));
        setInputTrackingNumber(record.trackingNumber || record.tracking_number || '');
      }
    }, [record]);

    if (!record) return null;

    const trackingNumber = inputTrackingNumber || record.trackingNumber || record.tracking_number;
    const shippingCompany = selectedCompany || record.shippingCompany || record.shipping_company;

    // 배송 상태 조회
    const handleTrackDelivery = async () => {
      if (!trackingNumber || !shippingCompany) {
        notify('택배사와 송장번호를 입력해주세요', { type: 'warning' });
        return;
      }

      setLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/orders/tracking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: record.id,
            trackingNumber,
            shippingCompany,
          }),
        });

        const result = await response.json();

        if (result.success) {
          setTrackingInfo({
            lastStatus: result.data.tracking?.last_status,
            lastTime: result.data.tracking?.last_time,
            events: result.data.trackingEvents || [],
          });
          notify(result.data.message || '배송 정보가 업데이트되었습니다', { type: 'success' });
          refresh();
        } else {
          notify(result.message || '배송 조회 실패', { type: 'error' });
        }
      } catch (error) {
        console.error('배송 조회 오류:', error);
        notify('배송 조회 중 오류가 발생했습니다', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    // 배송완료 수동 처리
    const handleMarkAsDelivered = async () => {
      if (!trackingNumber || !shippingCompany) {
        notify('택배사와 송장번호를 입력해주세요', { type: 'warning' });
        return;
      }

      setLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/orders/tracking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: record.id,
            trackingNumber,
            shippingCompany,
            forceDelivered: true,
          }),
        });

        const result = await response.json();

        if (result.success) {
          notify('배송완료로 처리되었습니다', { type: 'success' });
          refresh();
        } else {
          notify(result.message || '배송완료 처리 실패', { type: 'error' });
        }
      } catch (error) {
        console.error('배송완료 처리 오류:', error);
        notify('배송완료 처리 중 오류가 발생했습니다', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    // 배송 상태 단계 계산
    const getDeliveryStep = () => {
      const status = trackingInfo?.lastStatus?.toLowerCase() || '';
      if (status.includes('완료') || status.includes('delivered')) return 4;
      if (status.includes('배달') || status.includes('delivery')) return 3;
      if (status.includes('도착') || status.includes('배달지')) return 2;
      if (status.includes('이동') || status.includes('transit')) return 1;
      if (status.includes('인수') || status.includes('pickup')) return 0;
      return -1;
    };

    const deliveryStep = getDeliveryStep();

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
            <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            택배배송 조회
          </Typography>

          {/* 택배사 및 송장번호 입력 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">택배사 선택</MenuItem>
                {SHIPPING_COMPANIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <MuiTextField
              size="small"
              placeholder="송장번호"
              value={inputTrackingNumber}
              onChange={(e) => setInputTrackingNumber(e.target.value)}
              sx={{ width: 180 }}
            />

            <MuiButton
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={handleTrackDelivery}
              disabled={loading}
            >
              조회
            </MuiButton>

            {record.status !== 'DELIVERED' && (
              <MuiButton
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleMarkAsDelivered}
                disabled={loading}
              >
                배송완료 처리
              </MuiButton>
            )}
          </Box>

          {/* 배송 상태 스테퍼 */}
          {trackingInfo && (
            <>
              <Stepper
                activeStep={deliveryStep}
                alternativeLabel
                sx={{ mb: 3 }}
              >
                <Step completed={deliveryStep >= 0}>
                  <StepLabel>인수</StepLabel>
                </Step>
                <Step completed={deliveryStep >= 1}>
                  <StepLabel>이동중</StepLabel>
                </Step>
                <Step completed={deliveryStep >= 2}>
                  <StepLabel>배달지</StepLabel>
                </Step>
                <Step completed={deliveryStep >= 3}>
                  <StepLabel>배달중</StepLabel>
                </Step>
                <Step completed={deliveryStep >= 4}>
                  <StepLabel
                    StepIconProps={{
                      sx: deliveryStep >= 4 ? { color: 'success.main' } : {}
                    }}
                  >
                    완료
                  </StepLabel>
                </Step>
              </Stepper>

              {/* 배송 이력 테이블 */}
              {trackingInfo.events && trackingInfo.events.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    배송 이력
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>배송시간</TableCell>
                        <TableCell>현재위치</TableCell>
                        <TableCell>배송상태</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trackingInfo.events.slice().reverse().map((event, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(event.time).toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell>{event.location?.name || '-'}</TableCell>
                          <TableCell>{event.status?.name || event.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {(!trackingInfo.events || trackingInfo.events.length === 0) && trackingInfo.lastStatus && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  최신 상태: {trackingInfo.lastStatus}
                  {trackingInfo.lastTime && ` (${new Date(trackingInfo.lastTime).toLocaleString('ko-KR')})`}
                </Alert>
              )}
            </>
          )}

          {!trackingInfo && record.status === 'SHIPPED' && (
            <Alert severity="info">
              송장번호가 등록되어 있습니다. &quot;조회&quot; 버튼을 눌러 배송 상태를 확인하세요.
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Show title={<OrderShowTitle />}>
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {/* 주문 상태 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  주문 상태
                </Typography>
                <OrderStatusStepper />
              </CardContent>
            </Card>
          </Grid>

          {/* 주문 정보 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  주문 정보
                </Typography>
                <SimpleShowLayout>
                  <TextField source="orderNumber" label="주문번호" />
                  <DateField source="created_at" label="주문일시" showTime />
                  <FunctionField
                    label="주문상태"
                    render={(record: any) => <OrderStatusChip status={record.status} />}
                  />
                  <FunctionField
                    label="회원구분"
                    render={(record: any) => {
                      if (record?.userId) {
                        return <Chip label="회원" color="primary" size="small" />;
                      }
                      return <Chip label="비회원" color="default" size="small" />;
                    }}
                  />
                  <FunctionField
                    label="주문자명"
                    render={(record: any) => record?.userName || record?.ordererName || '-'}
                  />
                  <TextField source="userEmail" label="이메일" />
                  <TextField source="userPhone" label="연락처" />
                </SimpleShowLayout>
              </CardContent>
            </Card>
          </Grid>

          {/* 배송 정보 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  배송 정보
                </Typography>
                <SimpleShowLayout>
                  <FunctionField
                    label="수취인"
                    render={(record: any) => record?.recipientName || '-'}
                  />
                  <FunctionField
                    label="연락처"
                    render={(record: any) => record?.recipientPhone || '-'}
                  />
                  <FunctionField
                    label="배송주소"
                    render={(record: any) =>
                      record?.address
                        ? `[${record.postcode || ''}] ${record.address} ${record.addressDetail || ''}`
                        : '-'
                    }
                  />
                  <FunctionField
                    label="배송메모"
                    render={(record: any) => record?.deliveryRequest || '-'}
                  />
                  <FunctionField
                    label="송장번호"
                    render={(record: any) => record?.trackingNumber || '-'}
                  />
                  <FunctionField
                    label="택배사"
                    render={(record: any) => {
                      const companies: any = {
                        'CJ': 'CJ대한통운',
                        'HANJIN': '한진택배',
                        'LOTTE': '롯데택배',
                        'POST': '우체국택배',
                        'LOGEN': '로젠택배',
                        'CU': 'CU편의점택배',
                        'GS': 'GS편의점택배',
                      };
                      return companies[record?.shippingCompany] || record?.shippingCompany || '-';
                    }}
                  />
                </SimpleShowLayout>
              </CardContent>
            </Card>
          </Grid>

          {/* 택배배송 조회 */}
          <Grid item xs={12}>
            <DeliveryTrackingSection />
          </Grid>

          {/* 주문 상품 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  주문 상품
                </Typography>
                <FunctionField
                  render={(record: any) => {
                    const items = record?.items || [];
                    // 모바일: 카드 형태
                    return (
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        {items.map((item: any, idx: number) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              bgcolor: '#fafafa',
                              borderRadius: 1,
                              border: '1px solid #eee',
                            }}
                          >
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                              {item.product_name}
                            </Typography>
                            {item.option_name && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                옵션: {item.option_name}
                              </Typography>
                            )}
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="textSecondary">
                                {Number(item.discount_price || item.product_price || 0).toLocaleString()}원 x {item.quantity}개
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="primary">
                                {Number(item.subtotal || 0).toLocaleString()}원
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  }}
                />
                {/* 데스크톱: 테이블 형태 */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <ArrayField source="items">
                    <Datagrid
                      bulkActionButtons={false}
                      sx={{
                        '& .RaDatagrid-headerCell': {
                          textAlign: 'center',
                          fontWeight: 'bold',
                        },
                        '& .RaDatagrid-rowCell': {
                          textAlign: 'center',
                        },
                      }}
                    >
                      <TextField source="product_name" label="상품명" />
                      <FunctionField
                        label="옵션"
                        render={(record: any) => record?.option_name || '-'}
                      />
                      <FunctionField
                        label="단가"
                        render={(record: any) => {
                          const price = record?.discount_price || record?.product_price || 0;
                          return `${Number(price).toLocaleString()}원`;
                        }}
                      />
                      <NumberField source="quantity" label="수량" />
                      <FunctionField
                        label="소계"
                        render={(record: any) =>
                          `${Number(record?.subtotal || 0).toLocaleString()}원`
                        }
                      />
                    </Datagrid>
                  </ArrayField>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 결제 정보 */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  결제 정보
                </Typography>
                <Grid container spacing={2}>
                  {/* 모바일: 결제수단 간단 표시 */}
                  <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <FunctionField
                      render={(record: any) => {
                        const methods: any = {
                          CARD: '신용카드',
                          BANK_TRANSFER: '무통장입금',
                          VIRTUAL_ACCOUNT: '가상계좌',
                          PHONE: '휴대폰결제',
                          KAKAO_PAY: '카카오페이',
                          NAVER_PAY: '네이버페이',
                          TOSS: '토스',
                          TOSS_PAY: '토스페이',
                          TRANSFER: '계좌이체',
                        };
                        return (
                          <Box sx={{ bgcolor: '#fafafa', p: 1.5, borderRadius: 1, mb: 1 }}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" color="textSecondary">결제수단</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {methods[record?.paymentMethod] || record?.paymentMethod}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" color="textSecondary">결제일시</Typography>
                              <Typography variant="body2">
                                {record?.paidAt ? new Date(record.paidAt).toLocaleString('ko-KR') : '-'}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">결제상태</Typography>
                              <Chip
                                label={record?.isPaid ? '결제완료' : '미결제'}
                                size="small"
                                color={record?.isPaid ? 'success' : 'default'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        );
                      }}
                    />
                  </Grid>
                  {/* 데스크톱: 기존 레이아웃 */}
                  <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <SimpleShowLayout>
                      <FunctionField
                        label="결제수단"
                        render={(record: any) => {
                          const methods: any = {
                            CARD: '신용카드',
                            BANK_TRANSFER: '무통장입금',
                            VIRTUAL_ACCOUNT: '가상계좌',
                            PHONE: '휴대폰결제',
                            KAKAO_PAY: '카카오페이',
                            NAVER_PAY: '네이버페이',
                            TOSS: '토스',
                            TOSS_PAY: '토스페이',
                            TRANSFER: '계좌이체',
                          };
                          return methods[record.paymentMethod] || record.paymentMethod;
                        }}
                      />
                      <TextField source="paymentKey" label="결제키" />
                      <DateField source="paidAt" label="결제일시" showTime />
                      <BooleanField source="isPaid" label="결제완료" />
                    </SimpleShowLayout>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 1.5, md: 2 }, backgroundColor: '#f5f5f5' }}>
                      {/* 모바일: 간결한 레이아웃 */}
                      <FunctionField
                        render={(record: any) => (
                          <>
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" color="textSecondary">상품금액</Typography>
                                <Typography variant="body2">{Number(record?.subtotal || 0).toLocaleString()}원</Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" color="textSecondary">상품할인</Typography>
                                <Typography variant="body2" color="error">-{Number(record?.discountAmount || 0).toLocaleString()}원</Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" color="textSecondary">배송비</Typography>
                                <Typography variant="body2">+{Number(record?.shippingFee || 0).toLocaleString()}원</Typography>
                              </Box>
                              {(record?.couponDiscount > 0) && (
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <Typography variant="caption" color="textSecondary">쿠폰할인</Typography>
                                  <Typography variant="body2" color="error">-{Number(record?.couponDiscount || 0).toLocaleString()}원</Typography>
                                </Box>
                              )}
                              {(record?.pointUsed > 0) && (
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <Typography variant="caption" color="textSecondary">포인트</Typography>
                                  <Typography variant="body2" color="error">-{Number(record?.pointUsed || 0).toLocaleString()}원</Typography>
                                </Box>
                              )}
                              <Divider sx={{ my: 1 }} />
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" fontWeight="bold">총 결제금액</Typography>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  {Number(record?.finalAmount || 0).toLocaleString()}원
                                </Typography>
                              </Box>
                            </Box>
                            {/* 데스크톱: 기존 레이아웃 */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                              <Typography variant="body2" color="textSecondary">상품금액 (원가)</Typography>
                              <Typography variant="h6">{Number(record?.subtotal || 0).toLocaleString()}원</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="textSecondary">상품할인</Typography>
                              <Typography variant="h6" color="error">-{Number(record?.discountAmount || 0).toLocaleString()}원</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="textSecondary">배송비</Typography>
                              <Typography variant="h6">+{Number(record?.shippingFee || 0).toLocaleString()}원</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="textSecondary">쿠폰할인</Typography>
                              <Typography variant="h6" color="error">-{Number(record?.couponDiscount || 0).toLocaleString()}원</Typography>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="textSecondary">포인트사용</Typography>
                              <Typography variant="h6" color="error">-{Number(record?.pointUsed || 0).toLocaleString()}원</Typography>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="body1" fontWeight="bold">총 결제금액</Typography>
                              <Typography variant="h5" color="primary">{Number(record?.finalAmount || 0).toLocaleString()}원</Typography>
                            </Box>
                          </>
                        )}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 액션 버튼 */}
          <Grid item xs={12}>
            <OrderActionButtons />
          </Grid>
        </Grid>
      </Box>
    </Show>
  );
};

// 영문 코드 -> 한글 택배사명 변환 (OrderEdit용)
const CARRIER_CODE_TO_NAME_EDIT: { [key: string]: string } = {
  'CJ': 'CJ대한통운',
  'HANJIN': '한진택배',
  'LOTTE': '롯데택배',
  'POST': '우체국택배',
  'LOGEN': '로젠택배',
  'CU': 'CU편의점택배',
  'GS': 'GS편의점택배',
};

// 주문 수정 (상태 변경)
export const OrderEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const OrderEditTitle = () => {
    const record = useRecordContext();
    return <span>주문 관리: {record?.orderNumber}</span>;
  };

  const OrderEditToolbar = (props: any) => (
    <Toolbar {...props} sx={{ px: { xs: 1, md: 2 } }}>
      <SaveButton sx={{ flex: { xs: 1, md: 'none' } }} />
    </Toolbar>
  );

  // 택배사 코드 정규화 (영문 -> 한글)
  const formatCarrierCode = (value: string | undefined | null): string => {
    if (!value) return '';
    return CARRIER_CODE_TO_NAME_EDIT[value] || value;
  };

  return (
    <Edit title={<OrderEditTitle />}>
      <SimpleForm toolbar={<OrderEditToolbar />} sx={{ p: { xs: 1.5, md: 2 } }}>
        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
          주문 상태 변경 시 고객에게 알림이 발송됩니다.
        </Alert>
        <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
          주문 취소/환불 처리는 결제 수단에 따라 처리 기간이 다를 수 있습니다.
        </Alert>

        <Grid container spacing={{ xs: 1.5, md: 2 }}>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="status"
              label="주문상태"
              fullWidth
              size="small"
              choices={[
                { id: 'PAID', name: '결제완료' },
                { id: 'PROCESSING', name: '상품준비중' },
                { id: 'SHIPPED', name: '배송중' },
                { id: 'DELIVERED', name: '배송완료' },
                { id: 'CANCELLED', name: '취소' },
                { id: 'REFUNDED', name: '환불' },
              ]}
              validate={required()}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom fontSize={{ xs: '0.95rem', md: '1.25rem' }}>
              배송 정보
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <SelectInput
              source="shippingCompany"
              label="택배사"
              fullWidth
              size="small"
              format={formatCarrierCode}
              choices={[
                { id: 'CJ대한통운', name: 'CJ대한통운' },
                { id: '한진택배', name: '한진택배' },
                { id: '롯데택배', name: '롯데택배' },
                { id: '우체국택배', name: '우체국택배' },
                { id: '로젠택배', name: '로젠택배' },
                { id: '경동택배', name: '경동택배' },
                { id: '대신택배', name: '대신택배' },
                { id: '일양로지스', name: '일양로지스' },
                { id: '합동택배', name: '합동택배' },
                { id: 'GS포스트박스', name: 'GS포스트박스' },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="trackingNumber" label="송장번호" fullWidth size="small" />
          </Grid>

          <Grid item xs={12}>
            <TextInput
              source="adminMemo"
              label="관리자 메모"
              multiline
              rows={3}
              fullWidth
              size="small"
            />
          </Grid>

        </Grid>
      </SimpleForm>
    </Edit>
  );
};